import type {
  MediaDescriptor,
  MediaKind,
  MediaRendition,
} from "./media-types";
import type { ShareCapabilities } from "./sharing";

export const MEDIA_PROTOCOL_VERSION = 2 as const;
export const DEFAULT_MANIFEST_PAGE_SIZE = 20;
export const MAX_MANIFEST_PAGE_SIZE = 100;

export interface ShareMediaContributor {
  readonly avatarUrl?: string;
  readonly displayName: string;
  readonly mediaCount: number;
  readonly recipientId: string;
}

export interface AvailableShare {
  readonly shareId: string;
  readonly label: string;
  readonly ownerAvatarUrl?: string;
  readonly ownerEmail?: string;
  readonly ownerUsername?: string;
  readonly collaborators?: readonly ShareMediaContributor[];
  readonly originalMediaCount?: number;
  readonly revision: string;
  readonly capabilities: ShareCapabilities;
}

export interface MediaHello {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "media.hello";
  readonly versions: readonly [typeof MEDIA_PROTOCOL_VERSION];
}

export interface MediaHelloAck {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "media.hello.ack";
  readonly selectedVersion: typeof MEDIA_PROTOCOL_VERSION;
}

export interface SharesAvailable {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "shares.available";
  readonly shares: readonly AvailableShare[];
}

export interface ShareUpdated {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "share.updated";
  readonly share: AvailableShare;
}

export interface ShareRemoved {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "share.removed";
  readonly shareId: string;
  readonly reason:
    | "paused"
    | "revoked"
    | "expired"
    | "source-unavailable"
    | "capability-disabled";
}

export interface ManifestPageGet {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "manifest.page.get";
  readonly shareId: string;
  readonly requestId: string;
  readonly after: string | null;
  readonly limit: number;
  readonly revision: string | null;
}

export interface ManifestPage {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "manifest.page";
  readonly shareId: string;
  readonly requestId: string;
  readonly revision: string;
  readonly media: readonly MediaDescriptor[];
  readonly nextCursor: string | null;
  readonly totalMediaCount?: number;
}

export interface MediaGet {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "media.get";
  readonly shareId: string;
  readonly requestId: string;
  readonly mediaId: string;
  readonly rendition: MediaRendition;
}

export interface MediaQueued {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "media.queued";
  readonly shareId: string;
  readonly requestId: string;
}

export interface MediaStart {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "media.start";
  readonly shareId: string;
  readonly requestId: string;
  readonly mediaId: string;
  readonly kind: MediaKind;
  readonly rendition: MediaRendition;
  readonly mimeType: string;
  readonly byteLength: number;
  readonly sha256?: string;
}

export interface MediaProgress {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "media.progress";
  readonly shareId: string;
  readonly requestId: string;
  readonly transferredByteLength: number;
}

export interface MediaComplete {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "media.complete";
  readonly shareId: string;
  readonly requestId: string;
  readonly byteLength: number;
  readonly sha256: string;
}

export type MediaCancelReason =
  | "viewer-request"
  | "share-removed"
  | "feature-disabled"
  | "source-unavailable"
  | "connection-closed";

export interface MediaCancel {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "media.cancel";
  readonly shareId: string;
  readonly requestId: string;
  readonly reason: MediaCancelReason;
}

export type MediaErrorCode =
  | "unauthorized"
  | "not_found"
  | "unsupported_rendition"
  | "feature_disabled"
  | "invalid_request"
  | "revision_mismatch"
  | "source_unavailable"
  | "overloaded"
  | "cancelled"
  | "integrity_failed"
  | "timeout"
  | "internal";

export interface MediaError {
  readonly v: typeof MEDIA_PROTOCOL_VERSION;
  readonly type: "media.error";
  readonly shareId: string;
  readonly requestId: string;
  readonly code: MediaErrorCode;
  readonly retryable: boolean;
}

export type MediaControlMessage =
  | MediaHello
  | MediaHelloAck
  | SharesAvailable
  | ShareUpdated
  | ShareRemoved
  | ManifestPageGet
  | ManifestPage
  | MediaGet
  | MediaQueued
  | MediaStart
  | MediaProgress
  | MediaComplete
  | MediaCancel
  | MediaError;

export type ViewerMediaControlMessage =
  | MediaHello
  | ManifestPageGet
  | MediaGet
  | MediaCancel;

export type OwnerMediaControlMessage = Exclude<
  MediaControlMessage,
  ViewerMediaControlMessage
>;
